import { Anchor, Blockquote, Button, Card, Group, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { localised, useBrand } from "../../shared/brand/brand";
import { unwrap } from "../../shared/session/errors";

export function LandingPage() {
  const brand = useBrand();
  const { t, i18n } = useTranslation();
  const steps = [1, 2, 3] as const;
  return (
    <Stack gap="xl" py="xl">
      <Stack gap="sm" maw={720}>
        <Title order={1}>{brand.title ? localised(brand, brand.title, i18n.language) : brand.name}</Title>
        <Text size="xl" data-testid="tagline">
          {localised(brand, brand.tagline, i18n.language)}
        </Text>
        <Text c="dimmed">{t("landing.ussd", { code: brand.ussd_short_code })}</Text>
        <div>
          <Button size="md" component={Link} to="/signup">
            {t("landing.cta")}
          </Button>
        </div>
      </Stack>
      <section aria-labelledby="how">
        <Title order={2} id="how" mb="md">
          {t("landing.how_title")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          {steps.map((n) => (
            <Card key={n} withBorder padding="lg" bg="var(--brand-surface)">
              <Title order={3} size="h4" mb="xs">
                {t(`landing.step${n}_title`)}
              </Title>
              <Text>{t(`landing.step${n}_body`)}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </section>
      <Testimonials />
      <nav aria-labelledby="landing-links">
        <Title order={2} size="h4" id="landing-links" mb="xs">
          {t("landing.links_title")}
        </Title>
        <Group gap="lg" data-testid="landing-links">
          <Anchor component={Link} to="/pricing">
            {t("nav.pricing")}
          </Anchor>
          <Anchor component={Link} to="/terms">
            {t("nav.terms")}
          </Anchor>
          <Anchor component={Link} to="/privacy">
            {t("nav.privacy")}
          </Anchor>
        </Group>
      </nav>
    </Stack>
  );
}

/** Testimonials shows approved stories in the reader's language (FR-71); hidden when there are none. */
function Testimonials() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw" : "en";
  const stories = useQuery({
    queryKey: ["public", "testimonials", locale],
    queryFn: () => unwrap(api.GET("/public/testimonials", { params: { query: { locale } } })),
  });
  const items = stories.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section aria-labelledby="testimonials" data-testid="testimonials">
      <Title order={2} id="testimonials" mb="md">
        {t("landing.testimonials_title")}
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {items.map((s) => (
          <Blockquote key={s.id} cite={`— ${s.display_name}`} p="md" data-testid="testimonial" lang={s.locale}>
            <Text style={{ overflowWrap: "anywhere" }}>{s.body}</Text>
          </Blockquote>
        ))}
      </SimpleGrid>
    </section>
  );
}
